import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../utils/api';

/** Only this account can see orders, transfers, and student counts. */
export const SAEED_ADMIN_ID = '1f631d1e-2efe-41fa-bdab-60751666f12b';
export const SAEED_ADMIN_EMAIL = 'mrsaeedadmin@gmail.com';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'Teacher' | 'Moderator';
  privileges: string[];
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
  hasPrivilege: (privilege: string) => boolean;
  isTeacher: () => boolean;
  isModerator: () => boolean;
  isSaeedAdmin: () => boolean;
  getTeacherId: () => string | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Initialize user data synchronously from localStorage to avoid race conditions on page reload
  const [user, setUser] = useState<User | null>(() => {
    const token = authService.getToken();
    const userData = authService.getUserData();
    if (token && userData) {
      return userData;
    }
    return null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const token = authService.getToken();
    return !!token;
  });

  useEffect(() => {
    // Sync with authService in case it changes externally
    const token = authService.getToken();
    const userData = authService.getUserData();
    
    if (token && userData) {
      setUser(userData);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const login = (token: string, userData: User) => {
    authService.setAuthData(token, userData);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasPrivilege = (privilege: string): boolean => {
    if (!user) return false;
    
    // Moderators have full access
    if (user.role === 'Moderator') return true;
    
    // Teachers have specific privileges
    return user.privileges.includes(privilege);
  };

  const isTeacher = (): boolean => {
    return user?.role === 'Teacher';
  };

  const isModerator = (): boolean => {
    return user?.role === 'Moderator';
  };

  const isSaeedAdmin = (): boolean => {
    if (!user) return false;
    return (
      user.id === SAEED_ADMIN_ID ||
      user.email?.toLowerCase() === SAEED_ADMIN_EMAIL
    );
  };

  const getTeacherId = (): string | null => {
    return isTeacher() ? user?.id || null : null;
  };

  const value: UserContextType = {
    user,
    isAuthenticated,
    login,
    logout,
    hasPrivilege,
    isTeacher,
    isModerator,
    isSaeedAdmin,
    getTeacherId,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
